import { NewLeaverContract } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewLeaversModal/useNewLeaversModalController';
import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import { Option, Select } from '@dealstack/frontend/shared/components/forms/Select/Select';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { ButtonsRight } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import {
	CancelButton,
	CloseButtonSquare,
	DeleteButtonSquare,
	SaveButton,
	SaveButtonSquare,
	ThreeDotMenuIconButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Modal, ModalBox } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Cell, HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	DefaultTable,
	HeaderRow,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { CounterpartyType, DataCounterpartyTct } from '@dealstack/shared/lib/generated';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import { memo, useEffect, useMemo, useState } from 'react';
import {
	FormProvider,
	useFieldArray,
	useForm,
	useFormContext,
	useFormState,
} from 'react-hook-form';
import styled from 'styled-components';
import * as Yup from 'yup';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	className?: string;
	onClose: () => void;
	show: boolean;
	rowIndex: number;
	onSave: () => void;
}

type CounterpartyFormValues = {
	rows: DataCounterpartyTct[];
};
const NewLeaversContractCounterpartiesModal = ({ className, onClose, rowIndex, onSave }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const contractForm = useFormContext();
	const commonYup = useCommonYupValidation();
	const counterpartiesForm = useForm<CounterpartyFormValues>({
		defaultValues: {
			rows: contractForm.getValues(`contracts.${rowIndex}.counterparties`),
		},
		mode: 'all',
		resolver: yupResolver(
			Yup.object().shape({
				rows: Yup.array().of(
					Yup.object().shape({
						name: commonYup.string.required,
					})
				),
			})
		),
	});
	counterpartiesForm.watch();

	const { fields, append, remove } = useFieldArray({
		control: counterpartiesForm.control,
		name: `rows`,
		keyName: 'key' as 'id',
	});
	const onAddAnotherClicked = () => {
		append({
			id: generateUUID(),
			name: '',
			type: 'ONE_SIGNATORIES' as DataCounterpartyTct['type'],
		});
	};
	const onRemoveClicked = (id: string) => {
		const index = fields.findIndex((item) => item.id === id);
		if (index < 0) {
			return;
		}
		remove(index);
	};

	const onModalSave = () => {
		const contractKeyName = `contracts.${rowIndex}`;
		const contract: NewLeaverContract = contractForm.getValues(contractKeyName);
		const rows = counterpartiesForm.getValues('rows');
		if (!rows) {
			return;
		}

		const filteredArray = rows.filter((item) => item.name !== '');
		const updatedContract: NewLeaverContract = {
			...contract,
			counterparties: filteredArray,
		};
		contractForm.setValue(contractKeyName, updatedContract);
		onSave();
	};
	return (
		<Modal onClose={onClose} size="sm" className={className} closeButton={false}>
			<SectionHeader title={tMipCreate('contracts.modals.counterparties.title')} size="h4b" />
			<FormProvider {...counterpartiesForm}>
				<DefaultTableWrapper maxSize withEditableRows className={className}>
					<HeaderRow>
						<HeaderCell size={0.5}>#</HeaderCell>
						<HeaderCell customSize="1fr">
							{tMipCreate(
								'contracts.modals.counterparties.headers.counterparty_name'
							)}
						</HeaderCell>
						<HeaderCell customSize="1fr">
							{tMipCreate('contracts.modals.counterparties.headers.signatories')}
						</HeaderCell>
						<HeaderCell size={2.2} />
					</HeaderRow>
					<BodyRow>
						{fields?.map((item, index) => {
							return (
								<ContractCounterpartiesTableRow
									index={index}
									key={item.id}
									onRemoveClicked={() => {
										onRemoveClicked(item.id);
									}}
								/>
							);
						})}
					</BodyRow>
				</DefaultTableWrapper>
				<AddAnotherButton
					variant="text"
					size="medium"
					icon="add-circle"
					iconSize="md"
					iconPlacement="left"
					onClick={onAddAnotherClicked}
				>
					{tMipCreate('contracts.modals.counterparties.add')}
				</AddAnotherButton>
				<CounterpartiesModalButtons gap="md">
					<CancelButton onClick={onClose} />
					<SaveButton onClick={onModalSave} />
				</CounterpartiesModalButtons>
			</FormProvider>
		</Modal>
	);
};

const getSignatoriesOptionTranslation = (
	option: CounterpartyType,
	t: TranslateFunction<I18Namespaces.MIP_CREATE>
): string => {
	switch (option) {
		case CounterpartyType.ONE_SIGNATORIES: {
			return t('contracts.modals.counterparties.one_signatories');
		}
		case CounterpartyType.TWO_SIGNATORIES: {
			return t('contracts.modals.counterparties.two_signatories');
		}
	}
};

const SelectInvestorSignature = ({ name }: { name: string }) => {
	const tMipCreate = useTranslationMipCreate();

	return (
		<Select maxSize name={name}>
			<Option value={CounterpartyType.ONE_SIGNATORIES}>
				{getSignatoriesOptionTranslation(CounterpartyType.ONE_SIGNATORIES, tMipCreate)}
			</Option>
			<Option value={CounterpartyType.TWO_SIGNATORIES}>
				{getSignatoriesOptionTranslation(CounterpartyType.TWO_SIGNATORIES, tMipCreate)}
			</Option>
		</Select>
	);
};
const ContractCounterpartiesTableRow = styled(
	({
		className,
		onRemoveClicked,
		index,
	}: {
		className?: string;
		onRemoveClicked: () => void;
		index: number;
	}) => {
		const [isEdit, setIsEdit] = useState(false);
		const nameKey = useMemo(() => `rows.${index}.name`, [index]);
		const instructionKey = useMemo(() => `rows.${index}.type`, [index]);
		const [originalNameValue, setOriginalNameValue] = useState('');
		const [originalInstructionValue, setOriginalInstructionValue] = useState('');

		const methods = useFormContext();
		methods.watch();
		const nameValue = methods.getValues(nameKey);
		const instructionValue = methods.getValues(instructionKey);
		const { errors } = useFormState();
		const isInvalid = !!get(errors, nameKey);
		const tMipCreate = useTranslationMipCreate();

		const onSaveButtonClicked = async () => {
			const isValid = await methods.trigger(nameKey);
			if (!isValid) {
				return;
			}
			setIsEdit(false);
		};
		const onCloseButtonClicked = () => {
			if (originalNameValue === '') {
				onRemoveClicked();
			} else {
				methods.setValue(nameKey, originalNameValue ?? '');
				methods.setValue(instructionKey, originalInstructionValue ?? '');
				setIsEdit(false);
			}
		};
		const onEditButtonClicked = () => {
			setOriginalNameValue(nameValue);
			setOriginalInstructionValue(instructionValue);
			setIsEdit(true);
		};

		useEffect(() => {
			if (!nameValue && !isEdit) {
				setIsEdit(true);
			} else {
				setOriginalNameValue(nameValue);
				setOriginalInstructionValue(instructionValue);
			}
		}, []);
		return (
			<RowWrapper isInvalid={isInvalid} hasIndexColumn>
				<Row className={className}>
					<Cell>
						<Text bold variant="p-sm">
							{`${index + 1}.`}
						</Text>
					</Cell>
					<Cell>
						{isEdit ? (
							<StringInput
								name={nameKey}
								rhfOptions={{
									required: tMipCreate(
										'personal_data.tag_manager_modal.error_name_required'
									),
								}}
							/>
						) : (
							<Text variant="p-sm">{nameValue}</Text>
						)}
					</Cell>
					<Cell>
						{isEdit ? (
							<SelectInvestorSignature name={instructionKey} />
						) : (
							<Text elipsis variant="p-sm">
								{getSignatoriesOptionTranslation(instructionValue, tMipCreate)}
							</Text>
						)}
					</Cell>
					{isEdit ? (
						<ButtonsRight gap="sm">
							<SaveButtonSquare onClick={onSaveButtonClicked} />
							<CloseButtonSquare onClick={onCloseButtonClicked} />
							<DeleteButtonSquare onClick={onRemoveClicked} />
						</ButtonsRight>
					) : (
						<Cell align="right">
							<ThreeDotMenuIconButton onClick={onEditButtonClicked} />
						</Cell>
					)}
				</Row>
			</RowWrapper>
		);
	}
)``;

const DefaultTableWrapper = styled(DefaultTable)`
	overflow: visible;
`;

const StyledEditRow = styled(Row)`
	padding: 1em;
`;
const CounterpartiesModalButtons = styled(ButtonsRight)`
	padding-top: 2em;
`;
const AddAnotherButton = styled(Button)`
	padding-left: 0.25em;
`;
const Memoized = memo(NewLeaversContractCounterpartiesModal);
const Styled = styled(Memoized)`
	${AddAnotherButton} {
		margin-top: 0.5em;
	}

	${ModalBox} {
		width: 37.5rem;
	}

	${StyledEditRow} {
		background-color: unset;
	}

	${DefaultTableWrapper} {
		margin-top: 2em;
	}

	${Cell} {
		display: flex;
		align-items: center;
	}
`;

export { Styled as NewLeaversContractCounterpartiesModal };
